import * as React from "react";
import { Link } from "react-ui";

import { declint } from "../../../Engine/utils";
import {
    MULTILINE_FIELD_ADD_CLICK,
    MULTILINE_FIELD_EXPAND_CLICK,
    MULTILINE_FIELD_REMOVE_CLICK,
} from "../../metricsAction";
import { MetricsActionPayload, trackAction } from "../../Internals/Metrics/MetricsProvider";
import { pullContext } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { arrayIsNotEmpty, noop } from "../../../../../Common/TypingUtils";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    emptyModelPath,
    isModelPath,
    ModelPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { PushInstance } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { DataPasteContext, DatePasteStatus, MultilineDataPasteContext } from "../MultiLine/TableDataPasteContext";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { ModelHunk } from "../../../Engine/Core/ModelStoring/ModelHunk";
import { ExcelPasteUtils } from "../MultiLine/ExcelPasteUtils";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { Icon } from "../../Icons/Icon";

import { MultiLineFieldItem } from "./MultiLineFieldItem";
import { Root } from "./MultiLineField.styles";

function selector(props: MultiLineFieldProps): MetricsActionPayload {
    return { path: props.bindingPath, count: props.instances.length };
}

function expandSelector(props: MultiLineFieldProps, state: MultiLineFieldState): MetricsActionPayload {
    return { ...selector(props), expand: state.isExpanded };
}

export interface MultiLineFieldProps {
    addInstance: () => void;
    children: React.ReactNode;
    instances: readonly string[];
    removeInstance: (instance: string) => void;
    fillMultiline: (values: ModelHunk, newInstanceCount: number) => void;
    error: boolean;
    hintOnAdd: string;
    viewLength?: number;
    filtered: boolean;
    isReadOnly?: boolean;
    hasDataInOtherNodes: boolean;
    hasDataInFirstNode: boolean;
    bindingPath: AbsoluteResolvedModelPath;
    value?: string;
    pluralTitles: [string, string, string];
    backgroundColor?: string;
    disableButtons?: boolean;
    disableAppend?: boolean;
    columnsStructure?: AbsoluteModelPath[] | undefined;
}

interface MultiLineFieldState {
    isFocused: boolean;
    isExpanded: boolean;
}

export class MultiLineField extends React.Component<MultiLineFieldProps, MultiLineFieldState> {
    public static defaultProps = {
        error: false,
        hintOnAdd: "",
        pluralTitles: ["%s", "%s", "%s"] as [string, string, string],
    };

    public state: MultiLineFieldState = {
        isFocused: false,
        isExpanded: Boolean(this.props.error),
    };

    public componentDidMount(): void {
        const pastRegFunc = pullContext<MultilineDataPasteContext>(this).registerMultilinePasteFunc;
        if (pastRegFunc != undefined) {
            pastRegFunc((excelData, context, columnStartIndex) =>
                this.pasteTableData(excelData, context, "0", columnStartIndex)
            );
        }
    }

    private readonly pasteTableData = (
        excelData: string[][],
        context: DataPasteContext,
        rowStartName: string,
        columnStartIndex?: number
    ): DatePasteStatus => {
        if (this.props.columnsStructure == undefined) {
            return DatePasteStatus.Failure;
        }
        columnStartIndex =
            columnStartIndex ?? ExcelPasteUtils.findColumnStartIndex(context.bindingPath, this.props.columnsStructure);

        const rowStartIndex = ExcelPasteUtils.findRowStartIndex(rowStartName, this.props.instances);

        const columnNames = this.getColumnNames();

        const result = ExcelPasteUtils.pasteTableData(
            excelData,
            this.props.instances,
            columnNames,
            rowStartIndex,
            columnStartIndex
        );

        if (result === DatePasteStatus.Failure) {
            return result;
        } else {
            this.props.fillMultiline(result.data, result.newInstanceCount);
            return DatePasteStatus.SuccessAll;
        }
    };

    private readonly getColumnNames = (): ModelPath[] =>
        this.props.columnsStructure?.map(bp => {
            const parts = Array.from(bp.splitByMultiplicity());
            const last = IterUtils.last(parts);
            const ll = isModelPath(last) ? last : emptyModelPath();
            return arrayIsNotEmpty(parts) ? ll : emptyModelPath();
        }) ?? [];

    public UNSAFE_componentWillReceiveProps(nextProps: MultiLineFieldProps): void {
        if (nextProps.filtered !== this.props.filtered && nextProps.filtered) {
            this.setState({ isExpanded: true });
        }
        if (!this.props.bindingPath.isEquals(nextProps.bindingPath)) {
            this.setState({ isFocused: false });
        }
    }

    private readonly processTableDataPaste = (
        clipboardContent: DataTransfer,
        context: DataPasteContext,
        rowStartIndex: string,
        columnStartIndex?: number
    ): DatePasteStatus => {
        const excelData = ExcelPasteUtils.parseClipboardContent(clipboardContent);

        return this.pasteTableData(excelData, context, rowStartIndex, columnStartIndex);
    };

    public render(): JSX.Element {
        const [firstInstance, ...restInstances] = this.props.instances;
        const { isExpanded } = this.state;
        const hasData = this.props.hasDataInFirstNode === true || this.props.hasDataInOtherNodes === true;
        return this.props.isReadOnly && !hasData ? (
            <></>
        ) : (
            <TestPageObjectHandle
                id={this.props.bindingPath}
                pageObject={{
                    getType: () => "MultiLineField",
                    add: this.handleAddOnClick,
                    remove: this.props.removeInstance,
                    click: this.handleExpandOnClick,
                    getRowCount: () => this.props.instances.length,
                }}>
                <FocusManagementObserver
                    onRequestFocus={noop}
                    onRequestPathAvailability={this.handleRequestPathAvailability}>
                    <Root backgroundColor={this.props.backgroundColor}>
                        {firstInstance ? (
                            <DataPasteContext.Provider
                                key={`multiline_field_firstInstance`}
                                value={(dataTransfer, context) =>
                                    this.processTableDataPaste(dataTransfer, context, "0")
                                }>
                                <PushInstance instance={firstInstance} bindingPath={this.props.bindingPath}>
                                    <MultiLineFieldItem
                                        hint={this.props.hintOnAdd}
                                        icon={
                                            this.props.disableButtons || this.props.disableAppend
                                                ? undefined
                                                : "PlusIcon"
                                        }
                                        instance={firstInstance}
                                        key={firstInstance}
                                        onIconClick={this.handleAddOnClick}
                                        isReadOnly={this.props.isReadOnly}
                                        isLast={restInstances.length === 0}>
                                        {this.props.children}
                                    </MultiLineFieldItem>
                                </PushInstance>
                            </DataPasteContext.Provider>
                        ) : undefined}
                        {this.props.viewLength &&
                            restInstances.length >= this.props.viewLength &&
                            this.renderMoreItems(isExpanded, restInstances)}
                        {this.renderRestInstances(restInstances)}
                    </Root>
                </FocusManagementObserver>
            </TestPageObjectHandle>
        );
    }

    private readonly renderMoreItems = (isExpanded: boolean, restInstances: string[]) => {
        if (this.props.isReadOnly === true && this.props.hasDataInOtherNodes === false) {
            return <></>;
        } else {
            return (
                <Link onClick={this.handleExpandOnClick} data-tid="MoreItems">
                    {isExpanded ? (
                        <Icon name="ArrowShapeTriangleADownIcon" />
                    ) : (
                        <Icon name="ArrowShapeTriangleARightIcon" />
                    )}
                    {` и ещё ${declint(restInstances.length, this.props.pluralTitles)}`}
                </Link>
            );
        }
    };

    private expand(): void {
        if (!this.state.isExpanded) {
            this.handleExpand();
        }
    }

    private readonly handleRequestPathAvailability = async () => {
        this.expand();
    };

    @trackAction(MULTILINE_FIELD_ADD_CLICK, selector)
    public readonly handleAddOnClick = () => {
        this.props.addInstance();
        this.setState({ isExpanded: true, isFocused: true });
    };

    @trackAction(MULTILINE_FIELD_EXPAND_CLICK, expandSelector)
    public readonly handleExpandOnClick = () => {
        this.handleExpand();
    };

    private renderRestInstances(restInstances: string[]): undefined | JSX.Element | JSX.Element[] {
        if (
            this.props.viewLength == undefined ||
            this.state.isExpanded ||
            restInstances.length < this.props.viewLength
        ) {
            return restInstances.map((instance, index) => (
                <DataPasteContext.Provider
                    key={`multiline_field_${instance}_${index}`}
                    value={(dataTransfer, context) => this.processTableDataPaste(dataTransfer, context, instance)}>
                    <PushInstance instance={instance} key={instance} bindingPath={this.props.bindingPath}>
                        <MultiLineFieldItem
                            icon={this.props.disableButtons ? undefined : "XIcon"}
                            instance={instance}
                            isFocus={this.state.isFocused && restInstances.length - 1 === index}
                            isLast={false}
                            onIconClick={this.handleRemoveOnClick}
                            isReadOnly={this.props.isReadOnly}>
                            {this.props.children}
                        </MultiLineFieldItem>
                    </PushInstance>
                </DataPasteContext.Provider>
            ));
        }

        return undefined;
    }

    private readonly handleExpand = () => {
        this.setState(state => ({ isExpanded: !state.isExpanded, isFocused: false }));
    };

    @trackAction(MULTILINE_FIELD_REMOVE_CLICK, selector)
    private readonly handleRemoveOnClick = (instance: string) => {
        this.props.removeInstance(instance);
    };
}
